import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { v4 as uuidv4 } from 'uuid';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title as ChartTitle, Tooltip, Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import './App.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, ChartTitle, Tooltip, Legend,
);

// ── Config ────────────────────────────────────────────────────────────────────
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || 'http://localhost:3080/v1/callback';
const BACKEND      = process.env.REACT_APP_BACKEND      || 'http://localhost:8000';
const UPLOAD_CHUNK_BYTES = 150 * 1024;

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const TENANT_ID = process.env.REACT_APP_TENANT_ID;

const TIPS = [
  'Be specific — "Show cases by country for 2024" beats "show me data"',
  'Changing context? Open a new chat for best results',
  'Ask follow-up questions to drill deeper into results',
  'Use column names like "StatusName" or "LocationCountry" for precision',
  'Try aggregations — "average days open by status"',
  'You can ask "Show me the SQL" to see the generated query',
  'Export any result to CSV with one click',
  'Pivot tables let you cross-tabulate any two columns',
  'Ask about trends — "monthly case count for 2024"',
  'Filter by severity — "critical alert level cases"',
];

// ── OAuth helpers (no MSAL) ───────────────────────────────────────────────────

/** Build the Microsoft authorization URL and redirect the browser to it */
function redirectToMicrosoft() {
  const state = uuidv4();                        // CSRF protection
  localStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id:     CLIENT_ID,
    response_type: 'code',
    redirect_uri:  REDIRECT_URI,
    response_mode: 'query',
    scope:         'openid profile email User.Read offline_access',
    state,
    prompt:        'select_account',
  });

  window.location.href =
    `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize?${params}`;
}

/** Read token from localStorage */
function getStoredToken() {
  return localStorage.getItem('konet_token') || '';
}

/** Read user from localStorage */
function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('konet_user')); } catch { return null; }
}

/** Clear auth state */
function clearAuth() {
  localStorage.removeItem('konet_token');
  localStorage.removeItem('konet_user');
  localStorage.removeItem('konet_token_expiry');
  localStorage.removeItem('konet_user_photo');
}

async function uploadFileInChunks(file, token, authHdr) {
  const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_BYTES));
  const uploadId = uuidv4();
  let finalResponse = null;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const start = chunkIndex * UPLOAD_CHUNK_BYTES;
    const end = Math.min(start + UPLOAD_CHUNK_BYTES, file.size);
    const chunk = file.slice(start, end, file.type || 'application/octet-stream');

    const fd = new FormData();
    fd.append('file', chunk, file.name);
    fd.append('userToken', token);
    fd.append('uploadId', uploadId);
    fd.append('chunkIndex', String(chunkIndex));
    fd.append('totalChunks', String(totalChunks));
    fd.append('originalFileName', file.name);

    const res = await fetch(`${BACKEND}/api/upload`, {
      method: 'POST',
      headers: authHdr(),
      body: fd,
    });

    let payload = null;
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
    if (!res.ok) {
      throw new Error(payload?.detail || `Upload failed for chunk ${chunkIndex + 1}/${totalChunks}`);
    }
    finalResponse = payload;
  }

  return finalResponse;
}

/** Check if stored token is still valid (with 60s buffer) */
function tokenValid() {
  const expiry = parseInt(localStorage.getItem('konet_token_expiry') || '0', 10);
  return Date.now() < expiry - 60_000;
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
const MD = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        if (!inline && match) {
          return (
            <div className="cb-wrap">
              <div className="cb-hdr">
                <span className="cb-lang">{match[1]}</span>
                <button className="cb-copy"
                  onClick={() => navigator.clipboard.writeText(String(children))}>
                  Copy
                </button>
              </div>
              <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div"
                customStyle={{ margin: 0, borderRadius: '0 0 8px 8px', fontSize: '0.83rem', lineHeight: 1.55 }}
                {...props}>
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            </div>
          );
        }
        return <code className="ic" {...props}>{children}</code>;
      },
      table({ children }) {
        return <div className="tbl-wrap"><table>{children}</table></div>;
      },
    }}
  >{content}</ReactMarkdown>
);

const LOADING_MESSAGES = [
  "Brewing your response... grab a Coke!",
  "Crunching the numbers... time for a refreshment!",
  "Mixing the perfect answer... one moment!",
  "Pouring over the data... stay refreshed!",
  "Fizzing up some insights for you...",
  "Shaking up the analytics... almost ready!",
  "Adding the secret formula to your answer...",
  "Refreshing the data streams...",
  "Bubbling up the best results...",
  "Serving your answer ice cold...",
  "Uncapping fresh insights...",
  "The real thing is loading...",
];

const LoadingIndicator = () => {
  const [msgIdx, setMsgIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIdx(i => (i + 1) % LOADING_MESSAGES.length);
        setVisible(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="thinking-wrap">
      <div className="dots"><span /><span /><span /></div>
      <span className={`loading-message ${visible ? 'lm-in' : 'lm-out'}`}>
        {LOADING_MESSAGES[msgIdx]}
      </span>
    </div>
  );
};

const CopyButton = ({ text, className = 'msg-action-btn', title = 'Copy to clipboard' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button className={className} title={title} onClick={handleCopy}>
      {copied
        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      }
    </button>
  );
};

const Pill = ({ att, onRemove }) => {
  if (!att) return null;
  const name    = att._displayName || att.fileName || att.name || 'file';
  const rawName = att.fileName || att.name || '';
  const ext     = att._ext || rawName.split('.').pop() || '';
  const kb      = att.size ? `${(att.size / 1024).toFixed(1)} KB` : '';
  const icon    = att.isImage ? '🖼' : ['py','js','ts','jsx','tsx','cpp','java','go','rs','php','sql','html','css','json'].includes(ext) ? '💻' : '📄';
  return (
    <div className="pill">
      <span className="pill-icon">{icon}</span>
      <div className="pill-info">
        <span className="pill-name" title={rawName || name}>{name}</span>
        {(ext && !att._displayName) && <span className="pill-ext">.{ext}</span>}
        {kb && <span className="pill-kb">{kb}</span>}
      </div>
      {onRemove && <button className="pill-x" onClick={onRemove}>✕</button>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// METEORS — pure CSS animated background effect
// ══════════════════════════════════════════════════════════════════════════════
const Meteors = ({ number = 14 }) => {
  const meteors = useMemo(() => {
    return Array.from({ length: number }, (_, i) => ({
      id: i,
      left: (i * (800 / number) - 400) + 'px',
      delay: (Math.random() * 5).toFixed(2) + 's',
      duration: (Math.floor(Math.random() * 6) + 5) + 's',
    }));
  }, [number]);

  return (
    <div className="meteors-wrap">
      {meteors.map(m => (
        <span key={m.id} className="meteor"
          style={{ left: m.left, animationDelay: m.delay, animationDuration: m.duration }} />
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ANIMATED TIPS — cycling one-liners on the welcome screen
// ══════════════════════════════════════════════════════════════════════════════
const AnimatedTips = () => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % TIPS.length);
        setVisible(true);
      }, 400);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tips-container">
      <div className="tips-header">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M12 2a7 7 0 017 7c0 2.4-1.2 4.5-3 5.7V17a1 1 0 01-1 1H9a1 1 0 01-1-1v-2.3C6.2 13.5 5 11.4 5 9a7 7 0 017-7z"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 21h6M10 17v1M14 17v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        <span>Pro tip</span>
      </div>
      <p className={`tip-text ${visible ? 'tip-in' : 'tip-out'}`}>{TIPS[idx]}</p>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// DATA RESPONSE COMPONENT — table + chart + pivot
// ══════════════════════════════════════════════════════════════════════════════
const CHART_COLORS = [
  '#E61A27','#2563eb','#16a34a','#f59e0b','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#6366f1',
];

const TEXT_COLUMNS = ['Summary', 'Synopsis', 'CaseDetails'];

const TextAnalysisPanel = ({ columns, rows, onClose }) => {
  const analysis = useMemo(() => {
    // Find which text columns are present
    const textCols = columns.filter(c => TEXT_COLUMNS.includes(c));
    if (textCols.length === 0) return null;

    const allTexts = [];
    for (const col of textCols) {
      const idx = columns.indexOf(col);
      for (const row of rows) {
        const val = row[idx];
        if (val && typeof val === 'string' && val.trim()) allTexts.push(val.trim());
      }
    }

    if (allTexts.length === 0) return null;

    // Word frequency
    const stopWords = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','is','was','are','were','be','been','being','have','has','had','do','does','did','will','would','shall','should','may','might','can','could','that','this','these','those','it','its','not','no','nor','as','if','then','than','so','up','out','about','into','over','after','under','between','each','all','both','few','more','most','other','some','such','only','same','also','just','very','how','what','which','who','when','where','why','case','reported','coca','cola','company']);
    const wordMap = {};
    let totalWords = 0;
    let totalChars = 0;
    const lengths = [];

    for (const text of allTexts) {
      totalChars += text.length;
      const words = text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 2);
      totalWords += words.length;
      lengths.push(words.length);
      for (const w of words) {
        if (!stopWords.has(w)) wordMap[w] = (wordMap[w] || 0) + 1;
      }
    }

    const topWords = Object.entries(wordMap).sort((a, b) => b[1] - a[1]).slice(0, 20);
    const maxCount = topWords[0]?.[1] || 1;
    const avgLen = lengths.length ? Math.round(totalWords / lengths.length) : 0;
    const nullCount = rows.length - allTexts.length;

    // Unique words count
    const uniqueWords = Object.keys(wordMap).length;

    // Sentiment keywords
    const posWords = ['resolved','completed','successful','improvement','compliant','satisfactory','cooperation','corrected'];
    const negWords = ['violation','harassment','theft','fraud','abuse','misconduct','retaliation','terminated','fired','complaint','allegation','inappropriate'];
    let posCount = 0, negCount = 0;
    for (const text of allTexts) {
      const lower = text.toLowerCase();
      for (const w of posWords) if (lower.includes(w)) posCount++;
      for (const w of negWords) if (lower.includes(w)) negCount++;
    }

    return { allTexts, topWords, maxCount, totalWords, totalChars, avgLen, nullCount, uniqueWords, posCount, negCount, textCols };
  }, [columns, rows]);

  if (!analysis) return null;

  return (
    <div className="analysis-panel">
      <div className="analysis-header">
        <span className="analysis-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
          Text Analysis — {analysis.textCols.join(', ')}
        </span>
        <button className="analysis-close" onClick={onClose}>Close</button>
      </div>

      <div className="analysis-grid">
        <div className="analysis-stat">
          <div className="analysis-stat-label">Total entries</div>
          <div className="analysis-stat-val">{analysis.allTexts.length}</div>
        </div>
        <div className="analysis-stat">
          <div className="analysis-stat-label">Empty / NULL</div>
          <div className="analysis-stat-val">{analysis.nullCount}</div>
        </div>
        <div className="analysis-stat">
          <div className="analysis-stat-label">Total words</div>
          <div className="analysis-stat-val">{analysis.totalWords.toLocaleString()}</div>
        </div>
        <div className="analysis-stat">
          <div className="analysis-stat-label">Avg words / entry</div>
          <div className="analysis-stat-val">{analysis.avgLen}</div>
        </div>
        <div className="analysis-stat">
          <div className="analysis-stat-label">Unique keywords</div>
          <div className="analysis-stat-val">{analysis.uniqueWords.toLocaleString()}</div>
        </div>
        <div className="analysis-stat">
          <div className="analysis-stat-label">Total characters</div>
          <div className="analysis-stat-val">{analysis.totalChars.toLocaleString()}</div>
        </div>
      </div>

      {(analysis.posCount > 0 || analysis.negCount > 0) && (
        <div className="analysis-section">
          <div className="analysis-section-title">Sentiment Indicators</div>
          <div className="analysis-tags">
            {analysis.posCount > 0 && (
              <span className="analysis-tag" style={{ borderColor: 'rgba(22,163,74,.3)', background: 'rgba(22,163,74,.08)' }}>
                Positive mentions <span className="tag-count" style={{ color: '#16a34a' }}>{analysis.posCount}</span>
              </span>
            )}
            {analysis.negCount > 0 && (
              <span className="analysis-tag" style={{ borderColor: 'rgba(239,68,68,.3)', background: 'rgba(239,68,68,.08)' }}>
                Negative mentions <span className="tag-count" style={{ color: '#dc2626' }}>{analysis.negCount}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="analysis-section">
        <div className="analysis-section-title">Top Keywords</div>
        {analysis.topWords.slice(0, 12).map(([word, count]) => (
          <div className="analysis-bar-row" key={word}>
            <span className="analysis-bar-label">{word}</span>
            <div className="analysis-bar-track">
              <div className="analysis-bar-fill" style={{ width: `${(count / analysis.maxCount) * 100}%` }} />
            </div>
            <span className="analysis-bar-val">{count}</span>
          </div>
        ))}
      </div>

      {analysis.topWords.length > 12 && (
        <div className="analysis-section">
          <div className="analysis-section-title">More Keywords</div>
          <div className="analysis-tags">
            {analysis.topWords.slice(12).map(([word, count]) => (
              <span className="analysis-tag" key={word}>{word} <span className="tag-count">{count}</span></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DataResponse = ({ data, token, authHdr, userQuestion }) => {
  const { sql, columns, rows, chartConfig } = data;
  const [filters, setFilters] = useState({});
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [pivotMode, setPivotMode] = useState(false);
  const [pivotConfig, setPivotConfig] = useState({ row: '', col: '', val: '', agg: 'sum' });
  const [showSql, setShowSql] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null); // { col, text }
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportName, setExportName] = useState('cobc_data');
  const [aiSummary, setAiSummary] = useState(null);   // { text, loading, error }
  const [showAiSummary, setShowAiSummary] = useState(false);
  const hasTextCols = useMemo(() => columns.some(c => TEXT_COLUMNS.includes(c)), [columns]);

  const fetchAiSummary = async () => {
    // If we already have a cached summary, just toggle visibility
    if (aiSummary?.text) { setShowAiSummary(p => !p); return; }
    // If currently loading, ignore clicks
    if (aiSummary?.loading) return;
    setAiSummary({ text: '', loading: true, error: null });
    setShowAiSummary(true);
    try {
      const summaryRows = rows.slice(0, 500);
      const res = await fetch(`${BACKEND}/api/summarize-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHdr ? authHdr() : {}) },
        body: JSON.stringify({
          columns, rows: summaryRows, sql: sql || '',
          question: userQuestion || '', userToken: token || '',
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Summary failed');
      setAiSummary({ text: d.summary || 'No summary generated.', loading: false, error: null });
    } catch (e) {
      setAiSummary({ text: '', loading: false, error: e.message });
    }
  };

  // Filter rows
  const filtered = useMemo(() => {
    let r = rows;
    for (const [col, val] of Object.entries(filters)) {
      if (!val) continue;
      const idx = columns.indexOf(col);
      if (idx < 0) continue;
      const lv = val.toLowerCase();
      r = r.filter(row => String(row[idx] ?? '').toLowerCase().includes(lv));
    }
    return r;
  }, [rows, filters, columns]);

  // Sort rows
  const sorted = useMemo(() => {
    if (sortCol === null) return filtered;
    const idx = columns.indexOf(sortCol);
    if (idx < 0) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[idx], vb = b[idx];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number')
        return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [filtered, sortCol, sortDir, columns]);

  // Pivot table computation
  const pivotData = useMemo(() => {
    if (!pivotMode || !pivotConfig.row || !pivotConfig.col || !pivotConfig.val) return null;
    const ri = columns.indexOf(pivotConfig.row);
    const ci = columns.indexOf(pivotConfig.col);
    const vi = columns.indexOf(pivotConfig.val);
    if (ri < 0 || ci < 0 || vi < 0) return null;

    const map = {};
    const colVals = new Set();
    for (const row of filtered) {
      const rk = String(row[ri] ?? '');
      const ck = String(row[ci] ?? '');
      colVals.add(ck);
      if (!map[rk]) map[rk] = {};
      if (!map[rk][ck]) map[rk][ck] = [];
      const num = parseFloat(row[vi]);
      if (!isNaN(num)) map[rk][ck].push(num);
    }

    const colArr = [...colVals].sort();
    const agg = pivotConfig.agg;
    const aggFn = vals => {
      if (!vals || vals.length === 0) return 0;
      if (agg === 'count') return vals.length;
      if (agg === 'sum')   return vals.reduce((a, b) => a + b, 0);
      if (agg === 'avg')   return vals.reduce((a, b) => a + b, 0) / vals.length;
      if (agg === 'min')   return Math.min(...vals);
      if (agg === 'max')   return Math.max(...vals);
      return vals.length;
    };

    const pivotRows = Object.keys(map).sort().map(rk => ({
      label: rk,
      values: colArr.map(ck => aggFn(map[rk]?.[ck])),
    }));

    return { colHeaders: colArr, rows: pivotRows };
  }, [pivotMode, pivotConfig, columns, filtered]);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const doExportCsv = () => {
    const safeName = exportName.trim()
      ? (exportName.trim().endsWith('.csv') ? exportName.trim() : `${exportName.trim()}.csv`)
      : 'cobc_data.csv';
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [columns.map(esc).join(','), ...sorted.map(r => r.map(esc).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = safeName;
    a.click();
    setExportDialog(false);
  };

  // Chart
  const ChartComponent = chartConfig?.chartType && chartConfig.chartType !== 'none'
    ? { bar: Bar, line: Line, pie: Pie, doughnut: Doughnut }[chartConfig.chartType] : null;

  const chartData = useMemo(() => {
    if (!ChartComponent || !chartConfig?.xKey || !chartConfig?.yKey) return null;
    const xi = columns.indexOf(chartConfig.xKey);
    const yi = columns.indexOf(chartConfig.yKey);
    if (xi < 0 || yi < 0) return null;
    const labels = sorted.map(r => String(r[xi] ?? ''));
    const values = sorted.map(r => parseFloat(r[yi]) || 0);
    const isPieType = ['pie', 'doughnut'].includes(chartConfig.chartType);
    return {
      labels,
      datasets: [{
        label: chartConfig.yKey,
        data: values,
        backgroundColor: isPieType
          ? labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length])
          : 'rgba(230, 26, 39, 0.7)',
        borderColor: isPieType
          ? labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length])
          : '#E61A27',
        borderWidth: isPieType ? 2 : 2,
        borderRadius: isPieType ? 0 : 6,
      }],
    };
  }, [ChartComponent, chartConfig, columns, sorted]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: ['pie','doughnut'].includes(chartConfig?.chartType) },
      title: { display: !!chartConfig?.title, text: chartConfig?.title || '' },
    },
  };

  // No rows — only show SQL query and a message
  if (!rows || rows.length === 0) {
    return (
      <div className="data-response">
        <div className="sql-section">
          <div className="sql-badge-wrap">
            <pre className="sql-badge">{sql}</pre>
            <CopyButton text={sql} className="sql-copy-btn" title="Copy SQL" />
          </div>
        </div>
        <p style={{ fontSize: '.85rem', color: 'var(--t3)', margin: '.6rem 0 0' }}>
          No results found for this query.
        </p>
      </div>
    );
  }

  return (
    <div className="data-response">
      {/* SQL badge */}
      <div className="sql-section">
        <button className="sql-toggle" onClick={() => setShowSql(p => !p)}>
          {showSql ? 'Hide' : 'Show'} SQL
        </button>
        {showSql && (
          <div className="sql-badge-wrap">
            <pre className="sql-badge">{sql}</pre>
            <CopyButton text={sql} className="sql-copy-btn" title="Copy SQL" />
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="data-toolbar">
        <span className="data-count">{sorted.length} row{sorted.length !== 1 ? 's' : ''}</span>
        <button className="btn-export" onClick={() => { setExportName('cobc_data'); setExportDialog(true); }}>Export CSV</button>
        <button className={`pivot-toggle ${pivotMode ? 'active' : ''}`}
          onClick={() => setPivotMode(p => !p)}>
          Pivot Table
        </button>
        {hasTextCols && (
          <button className={`analyze-btn ${showAnalysis ? 'active' : ''}`}
            onClick={() => setShowAnalysis(p => !p)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            {showAnalysis ? 'Hide' : 'Analyze'} Summary
          </button>
        )}
        <button className={`ai-summary-btn ${showAiSummary && aiSummary?.text ? 'active' : ''}`}
          onClick={fetchAiSummary}
          disabled={aiSummary?.loading}>
          {aiSummary?.loading ? (
            <><span className="spin" style={{ width: 12, height: 12 }} /> Generating...</>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              {showAiSummary && aiSummary?.text ? 'Hide' : 'AI'} Summary
            </>
          )}
        </button>
      </div>

      {/* AI Summary panel */}
      {showAiSummary && aiSummary?.text && (
        <div className="ai-summary-panel">
          <div className="ai-summary-head">
            <span className="ai-summary-title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#E61A27" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              AI-Powered Insights
            </span>
            <button className="analysis-close" onClick={() => setShowAiSummary(false)}>Close</button>
          </div>
          <div className="ai-summary-body">
            <MD content={aiSummary.text} />
          </div>
        </div>
      )}
      {showAiSummary && aiSummary?.error && (
        <div className="ai-summary-error">
          Failed to generate summary: {aiSummary.error}
        </div>
      )}

      {/* Text analysis panel */}
      {showAnalysis && hasTextCols && (
        <TextAnalysisPanel columns={columns} rows={sorted} onClose={() => setShowAnalysis(false)} />
      )}

      {/* Pivot controls */}
      {pivotMode && (
        <div className="pivot-controls">
          <label>Row:
            <select value={pivotConfig.row} onChange={e => setPivotConfig(p => ({ ...p, row: e.target.value }))}>
              <option value="">--</option>
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Column:
            <select value={pivotConfig.col} onChange={e => setPivotConfig(p => ({ ...p, col: e.target.value }))}>
              <option value="">--</option>
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Value:
            <select value={pivotConfig.val} onChange={e => setPivotConfig(p => ({ ...p, val: e.target.value }))}>
              <option value="">--</option>
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Agg:
            <select value={pivotConfig.agg} onChange={e => setPivotConfig(p => ({ ...p, agg: e.target.value }))}>
              {['sum','count','avg','min','max'].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* Pivot result table */}
      {pivotData && (
        <div className="data-table-wrap pivot-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>{pivotConfig.row}</th>
                {pivotData.colHeaders.map(c => <th key={c}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {pivotData.rows.map((r, i) => (
                <tr key={i}>
                  <td className="pivot-row-label">{r.label}</td>
                  {r.values.map((v, j) => <td key={j}>{typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Chart */}
      {ChartComponent && chartData && (
        <div className="chart-container">
          <ChartComponent data={chartData} options={chartOptions} />
        </div>
      )}

      {/* Data table */}
      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col} onClick={() => handleSort(col)}
                  className={sortCol === col ? `sorted-${sortDir}` : ''}>
                  {col}
                </th>
              ))}
            </tr>
            <tr className="filter-row">
              {columns.map(col => (
                <th key={`f-${col}`}>
                  <input
                    type="text"
                    placeholder="Filter…"
                    value={filters[col] || ''}
                    onChange={e => setFilters(p => ({ ...p, [col]: e.target.value }))}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 200).map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const colName = columns[ci];
                  const isText = TEXT_COLUMNS.includes(colName);
                  const val = cell != null ? String(cell) : '';
                  if (isText && val.length > 60) {
                    return (
                      <td key={ci} className="td-text"
                        onClick={() => setExpandedCell({ col: colName, text: val })}
                        title="Click to expand">
                        {val.slice(0, 60)}
                      </td>
                    );
                  }
                  return <td key={ci}>{val}</td>;
                })}
              </tr>
            ))}
            {sorted.length > 200 && (
              <tr><td colSpan={columns.length} className="truncation-note">
                Showing 200 of {sorted.length} rows. Export CSV for full data.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cell expand modal */}
      {expandedCell && (
        <div className="cell-modal-overlay" onClick={() => setExpandedCell(null)}>
          <div className="cell-modal" onClick={e => e.stopPropagation()}>
            <div className="cell-modal-head">
              <span className="cell-modal-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                {expandedCell.col}
              </span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <CopyButton text={expandedCell.text} className="msg-action-btn" title="Copy text" />
                <button className="cell-modal-close" onClick={() => setExpandedCell(null)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <div className="cell-modal-body">{expandedCell.text}</div>
          </div>
        </div>
      )}

      {/* Export CSV dialog */}
      {exportDialog && (
        <div className="cell-modal-overlay" onClick={() => setExportDialog(false)}>
          <div className="export-dialog" onClick={e => e.stopPropagation()}>
            <div className="export-dialog-head">
              <span className="export-dialog-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export to CSV
              </span>
              <button className="cell-modal-close" onClick={() => setExportDialog(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="export-dialog-body">
              <div className="export-stats">
                <div className="export-stat">
                  <span className="export-stat-num">{sorted.length.toLocaleString()}</span>
                  <span className="export-stat-label">Rows</span>
                </div>
                <div className="export-stat">
                  <span className="export-stat-num">{columns.length}</span>
                  <span className="export-stat-label">Columns</span>
                </div>
                <div className="export-stat">
                  <span className="export-stat-num">{(sorted.length * columns.length).toLocaleString()}</span>
                  <span className="export-stat-label">Cells</span>
                </div>
              </div>
              <label className="export-label">File name</label>
              <div className="export-input-wrap">
                <input
                  className="export-input"
                  type="text"
                  value={exportName}
                  onChange={e => setExportName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') doExportCsv(); }}
                  autoFocus
                  placeholder="cobc_data"
                />
                <span className="export-ext">.csv</span>
              </div>
              <p className="export-note">
                {Object.keys(filters).some(k => filters[k]) && (
                  <span className="export-filter-badge">Filtered</span>
                )}
                {sortCol && (
                  <span className="export-filter-badge">Sorted by {sortCol}</span>
                )}
                Export includes all {sorted.length} rows currently shown.
              </p>
            </div>
            <div className="export-dialog-foot">
              <button className="fb-cancel-btn" onClick={() => setExportDialog(false)}>Cancel</button>
              <button className="export-download-btn" onClick={doExportCsv}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════════════════════════
const Login = ({ loading, error }) => (
  <div className="login-bg">
    <div className="blob b1" /><div className="blob b2" /><div className="blob b3" />
    <div className="login-card">
      <div className="lc-logo">
        <img src="/logo.png" alt="KOGNITION COBC" className="lc-logo-img" />
      </div>

      <h1 className="lc-name">KOGNITION COBC</h1>
      <p className="lc-sub">AI-powered workspace</p>

      <div className="lc-sep"><span>Sign in to continue</span></div>

      {loading ? (
        <div className="ms-btn" style={{ cursor: 'default', justifyContent: 'center' }}>
          <span className="spin" />  Completing sign-in…
        </div>
      ) : (
        <button className="ms-btn" onClick={redirectToMicrosoft}>
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
            <rect x="1"  y="1"  width="9" height="9" fill="#F35325"/>
            <rect x="11" y="1"  width="9" height="9" fill="#81BC06"/>
            <rect x="1"  y="11" width="9" height="9" fill="#05A6F0"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFBA08"/>
          </svg>
          Continue with Microsoft
        </button>
      )}

      {error && <p className="lc-err">⚠ {error}</p>}

      <p className="lc-foot">
        Uses your organisation's Microsoft account.<br />
        No credentials are stored by this application.
      </p>
    </div>
  </div>
);


// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [user,         setUser]         = useState(getStoredUser);
  const [token,        setToken]        = useState(getStoredToken);
  const [authLoading,  setAuthLoading]  = useState(false);
  const [authError,    setAuthError]    = useState('');

  // ── Handle OAuth callback (/v1/callback?code=...&state=...) ───────────────
  useEffect(() => {
    const url    = new URL(window.location.href);
    const code   = url.searchParams.get('code');
    const state  = url.searchParams.get('state');
    const errMsg = url.searchParams.get('error_description');

    if (errMsg) {
      setAuthError(decodeURIComponent(errMsg));
      window.history.replaceState({}, '', '/');
      return;
    }

    if (!code) return;   // not a callback — normal page load

    // Validate state to prevent CSRF
    const savedState = localStorage.getItem('oauth_state');
    if (state !== savedState) {
      // State mismatch — silently clean URL and show fresh login screen.
      // This can happen when the user returns to a stale callback URL,
      // localStorage was cleared, or the session expired mid-flow.
      console.warn('OAuth state mismatch — clearing and showing login.');
      localStorage.removeItem('oauth_state');
      window.history.replaceState({}, '', '/');
      return;
    }
    localStorage.removeItem('oauth_state');

    // Exchange code for token via our backend
    setAuthLoading(true);
    fetch(`${BACKEND}/api/auth/callback`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ code, redirect_uri: REDIRECT_URI }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);

        // Store token + user + expiry
        localStorage.setItem('konet_token',        data.accessToken);
        localStorage.setItem('konet_user',         JSON.stringify(data.user));
        localStorage.setItem('konet_token_expiry', String(Date.now() + 7200 * 1000));

        setToken(data.accessToken);
        setUser(data.user);

        // Clean URL — remove ?code=...&state=... 
        window.history.replaceState({}, '', '/');
      })
      .catch(err => {
        setAuthError(err.message || 'Sign-in failed. Please try again.');
        window.history.replaceState({}, '', '/');
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = () => {
    clearAuth();
    setToken(''); setUser(null);
    // Optionally sign out of Microsoft too
    window.location.href =
      `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/logout` +
      `?post_logout_redirect_uri=${encodeURIComponent('http://localhost:3080')}`;
  };

  // ── Not logged in ──────────────────────────────────────────────────────────
  if (!user || !token || !tokenValid()) {
    return <Login loading={authLoading} error={authError} />;
  }

  return <ChatUI user={user} token={token} onLogout={logout} />;
}

// ── Date grouping helper ─────────────────────────────────────────────────────
function groupThreadsByDate(threads) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart); yesterdayStart.setDate(todayStart.getDate() - 1);
  const weekStart = new Date(todayStart); weekStart.setDate(todayStart.getDate() - 7);
  const monthStart = new Date(todayStart); monthStart.setDate(todayStart.getDate() - 30);

  const groups = [
    { label: 'Today',        threads: [] },
    { label: 'Yesterday',    threads: [] },
    { label: 'Last 7 Days',  threads: [] },
    { label: 'Last 30 Days', threads: [] },
    { label: 'Older',        threads: [] },
  ];

  for (const t of threads) {
    const d = t.updatedAt ? new Date(t.updatedAt) : new Date();
    if (d >= todayStart)          groups[0].threads.push(t);
    else if (d >= yesterdayStart)  groups[1].threads.push(t);
    else if (d >= weekStart)      groups[2].threads.push(t);
    else if (d >= monthStart)     groups[3].threads.push(t);
    else                          groups[4].threads.push(t);
  }

  return groups.filter(g => g.threads.length > 0);
}

// ── Assistant pre-fed questions ───────────────────────────────────────────────
const ASSISTANT_QUESTIONS = [
  { category: 'Quick Stats', questions: [
    'How many distinct cases have been reported?',
    'Cases by alert level',
    'Average days open by status',
  ]},
  { category: 'Trends', questions: [
    'Monthly case count trend for the current year',
    'Show cases by country for the last 6 months',
  ]},
  { category: 'Geographic', questions: [
    'Top 10 countries by case count',
    'Cases by location state for the US',
  ]},
  { category: 'Issue Analysis', questions: [
    'Closure rate by primary issue type',
    'What is the aging analysis of open cases?',
  ]},
  { category: 'Advanced', questions: [
    'Cases reported via hotline vs web',
    'Anonymous vs non-anonymous reporting rate',
  ]},
];

// ══════════════════════════════════════════════════════════════════════════════
// CHAT UI (only rendered when authenticated)
// ══════════════════════════════════════════════════════════════════════════════
function ChatUI({ user, token, onLogout }) {
  const [threads,  setThreads]  = useState([]);
  const [active,   setActive]   = useState(null);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sbOpen,   setSbOpen]   = useState(true);
  const [err,      setErr]      = useState(null);
  const [pending,  setPending]  = useState(null);
  const [userPhoto, setUserPhoto] = useState(() => localStorage.getItem('konet_user_photo') || null);
  const [sbTab, setSbTab] = useState('chat');
  const [feedbacks, setFeedbacks] = useState({});
  const [fbDialog, setFbDialog] = useState(null); // { msgId, step: 'reason' }

  const fileRef = useRef();
  const endRef  = useRef();
  const taRef   = useRef();
  const msgs    = active?.messages || [];

  // ── Task 1: Total token usage for current thread ──────────────────────────
  const totalUsage = useMemo(() => {
    let inp = 0, out = 0;
    for (const m of msgs) {
      if (m.usage) {
        inp += m.usage.input_tokens || 0;
        out += m.usage.output_tokens || 0;
      }
    }
    return { input_tokens: inp, output_tokens: out };
  }, [msgs]);

  // ── Task 2: Fetch user profile photo ──────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    // Try cached photo first
    const cached = localStorage.getItem('konet_user_photo');
    if (cached) { setUserPhoto(cached); return; }

    fetch(`${BACKEND}/api/auth/photo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.photo) {
          localStorage.setItem('konet_user_photo', data.photo);
          setUserPhoto(data.photo);
        }
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, loading]);
  useEffect(() => {
    const ta = taRef.current; if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  }, [input]);

  // ── Auth header helper ─────────────────────────────────────────────────────
  const authHdr = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ── Load threads ───────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/threads`, { headers: authHdr() });
      if (res.ok) {
        const d = await res.json();
        setThreads((d.threads || []).map(t => ({ ...t, id: t.threadId, messages: [], loaded: false })));
      }
    } catch (e) { console.warn('threads:', e.message); }
  }, [authHdr]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Open thread ────────────────────────────────────────────────────────────
  const openThread = async t => {
    if (t.loaded) { setActive(t); return; }
    try {
      const res = await fetch(`${BACKEND}/api/threads/${t.id}`, { headers: authHdr() });
      if (res.ok) {
        const d   = await res.json();
        const ful = { ...t, messages: d.messages || [], loaded: true };
        setThreads(p => p.map(x => x.id === t.id ? ful : x));
        setActive(ful); return;
      }
    } catch {}
    setActive({ ...t, loaded: true });
  };

  const newThread = () => {
    const t = { id: uuidv4(), title: 'New conversation', messages: [], loaded: true };
    setThreads(p => [t, ...p]); setActive(t);
    setInput(''); setErr(null); setPending(null);
  };

  const delThread = async (id, e) => {
    e.stopPropagation();
    setThreads(p => p.filter(t => t.id !== id));
    if (active?.id === id) setActive(null);
    try {
      await fetch(`${BACKEND}/api/threads/${id}`, {
        method: 'DELETE',
        headers: authHdr(),
      });
    } catch (err) {
      console.warn('delete thread:', err.message);
    }
  };

  const saveThread = async thread => {
    try {
      const stripped = thread.messages.map(m => {
        // Truncate data rows for persistence (keep first 100)
        if (m.type === 'data' && m.data) {
          return { ...m, data: { ...m.data, rows: m.data.rows.slice(0, 100) } };
        }
        return {
          ...m,
          content: Array.isArray(m.content)
            ? m.content.map(b =>
                b.type === 'image' && b.source?.type === 'base64'
                  ? { ...b, source: { ...b.source, data: '[truncated]' } } : b)
            : m.content,
        };
      });
      await fetch(`${BACKEND}/api/threads/save`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHdr() },
        body:    JSON.stringify({ threadId: thread.id, title: thread.title, messages: stripped, userToken: token }),
      });
    } catch {}
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const send = useCallback(async (override) => {
    const text = (override || input).trim();
    if ((!text && !pending) || loading) return;
    setErr(null);

    let thread = active;
    if (!thread) {
      thread = { id: uuidv4(), title: text.slice(0, 50) || 'File upload', messages: [], loaded: true };
      setThreads(p => [thread, ...p]); setActive(thread);
    }

    // upload
    let att = null;
    if (pending) {
      try {
        att = await uploadFileInChunks(pending, token, authHdr);
      } catch (e) { console.warn('upload:', e.message); }
      setPending(null);
    }

    const blocks = [];
    if (att?.isImage && att.base64Data)
      blocks.push({ type: 'image', source: { type: 'base64', media_type: att.mediaType || 'image/jpeg', data: att.base64Data } });
    else if (att?.textContent) {
      const ext = (att.fileName || '').split('.').pop() || 'txt';
      blocks.push({ type: 'text', text: `\`\`\`${ext}\n${att.textContent}\n\`\`\`` });
    }
    if (text) blocks.push({ type: 'text', text });
    if (!blocks.length) return;

    const uMsg = {
      id: uuidv4(), role: 'user', content: blocks, displayText: text,
      attachment: att ? {
        fileName: att.fileName, size: att.size, isImage: att.isImage,
        previewUrl: att.isImage && att.base64Data ? `data:${att.mediaType};base64,${att.base64Data}` : null,
      } : null,
    };

    const newMsgs = [...thread.messages, uMsg];
    const title   = thread.messages.length === 0 ? (text || att?.fileName || 'File').slice(0, 50) : thread.title;
    const updated = { ...thread, title, messages: newMsgs };
    setThreads(p => p.map(t => t.id === thread.id ? updated : t));
    setActive(updated); setInput(''); setLoading(true);

    try {
      const res = await fetch(`${BACKEND}/api/query`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHdr() },
        body: JSON.stringify({
          question: text,
          userToken: token,
          chatHistory: thread.messages.slice(-6).map(m => ({
            role: m.role,
            content: m.displayText || (typeof m.content === 'string' ? m.content : m.data?.sql || ''),
          })),
        }),
      });
      let d;
      try {
        d = await res.json();
      } catch {
        throw new Error(`Server error (${res.status}): ${await res.text().catch(() => 'no details')}`);
      }
      if (!res.ok) throw new Error(d.detail || `Error ${res.status}`);

      let aiMsg;
      if (d.type === 'data') {
        aiMsg = {
          id: uuidv4(), role: 'assistant', type: 'data',
          data: { sql: d.sql, columns: d.columns, rows: d.rows, chartConfig: d.chartConfig },
          usage: d.usage || null,
        };
      } else {
        aiMsg = {
          id: uuidv4(), role: 'assistant', content: d.message || d.reply || '', type: 'greeting',
          usage: d.usage || null,
        };
      }

      const final = { ...updated, messages: [...newMsgs, aiMsg] };
      setThreads(p => p.map(t => t.id === thread.id ? final : t));
      setActive(final); saveThread(final);

      // Generate AI summary title after first exchange
      if (thread.messages.length === 0 && text) {
        fetch(`${BACKEND}/api/threads/summarize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHdr() },
          body: JSON.stringify({ text }),
        })
          .then(r => r.json())
          .then(data => {
            if (data.title) {
              // Enforce max 7 words for sidebar title
              const trimmed = data.title.split(/\s+/).slice(0, 7).join(' ');
              const titled = { ...final, title: trimmed };
              setThreads(p => p.map(t => t.id === thread.id ? { ...t, title: trimmed } : t));
              setActive(prev => prev?.id === thread.id ? { ...prev, title: trimmed } : prev);
              saveThread(titled);
            }
          })
          .catch(() => {});
      }
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  }, [input, pending, loading, active, token, user, authHdr]);

  const kd = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  // ── Paste handler — large pastes become file attachments ──────────────────
  const PASTE_LINE_THRESHOLD = 8;    // lines
  const PASTE_CHAR_THRESHOLD = 500;  // characters

  const onPaste = e => {
    const text = e.clipboardData?.getData('text/plain') || '';
    const lines = text.split('\n').length;

    if (lines < PASTE_LINE_THRESHOLD && text.length < PASTE_CHAR_THRESHOLD) {
      return; // short paste — let it go through normally
    }

    e.preventDefault(); // stop text from appearing in textarea

    // Detect language for filename extension
    const extMap = [
      [/^\s*import |from ['"]|export (default|const|function)/m,  'js'],
      [/def |import |class |:\s*$|\bself\b/m,                    'py'],
      [/^\s*<[a-zA-Z]/m,                                          'html'],
      [/^\s*\{[\s\S]*"[^"]+"\s*:/m,                             'json'],
      [/SELECT |INSERT |UPDATE |DELETE |CREATE TABLE/im,           'sql'],
      [/#include|int main|std::|cout|endl/m,                       'cpp'],
      [/^\s*(public|private|class|void|import java)/m,            'java'],
      [/^\s*(func |var |let |import ")/m,                         'go'],
      [/^\s*(fn |use |let mut|impl |pub struct)/m,                'rs'],
      [/\$[a-zA-Z_]|echo |\?>/m,                                 'php'],
      [/.*/,                                                        'txt'],
    ];

    let ext = 'txt';
    for (const [regex, e2] of extMap) {
      if (regex.test(text)) { ext = e2; break; }
    }

    const lineCount = lines;
    const charCount = text.length;
    const label = lineCount >= PASTE_LINE_THRESHOLD
      ? `Pasted code (${lineCount} lines)`
      : `Pasted text (${charCount} chars)`;

    const fileName = `pasted-${Date.now()}.${ext}`;

    // Create a real File object so it uploads identically to a picked file
    const file = new File([text], fileName, { type: 'text/plain' });

    // Attach extra display metadata
    file._displayName = label;
    file._ext = ext;

    setPending(file);
  };

  const copyToNewChat = (text) => {
    const t = { id: uuidv4(), title: 'New conversation', messages: [], loaded: true };
    setThreads(p => [t, ...p]); setActive(t);
    setInput(text); setErr(null); setPending(null);
  };

  const pendingQuestionRef = useRef(null);

  const askAssistantQuestion = (question) => {
    const t = { id: uuidv4(), title: question.slice(0, 50), messages: [], loaded: true };
    setThreads(p => [t, ...p]); setActive(t);
    setInput(''); setErr(null); setPending(null);
    pendingQuestionRef.current = question;
  };

  useEffect(() => {
    if (pendingQuestionRef.current && active && !loading) {
      const q = pendingQuestionRef.current;
      pendingQuestionRef.current = null;
      send(q);
    }
  }, [active, loading, send]);

  // Helper: find the user question that preceded an assistant message
  const getQAPair = (msgId) => {
    const allMsgs = active?.messages || [];
    const idx = allMsgs.findIndex(m => m.id === msgId);
    if (idx < 0) return { question: '', response: '' };
    const assistantMsg = allMsgs[idx];
    // Find the preceding user message
    let question = '';
    for (let i = idx - 1; i >= 0; i--) {
      if (allMsgs[i].role === 'user') {
        question = allMsgs[i].displayText || (typeof allMsgs[i].content === 'string' ? allMsgs[i].content : '');
        break;
      }
    }
    // Get response text
    let response = '';
    if (assistantMsg.type === 'data' && assistantMsg.data) {
      response = `SQL: ${assistantMsg.data.sql || ''} | ${assistantMsg.data.rows?.length || 0} rows returned | Columns: ${(assistantMsg.data.columns || []).join(', ')}`;
    } else {
      response = typeof assistantMsg.content === 'string' ? assistantMsg.content : '';
    }
    return { question, response };
  };

  const sendFeedback = async (msgId, feedback, reason = '') => {
    if (feedbacks[msgId]) return;
    setFeedbacks(p => ({ ...p, [msgId]: feedback }));
    const { question, response } = getQAPair(msgId);
    try {
      await fetch(`${BACKEND}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHdr() },
        body: JSON.stringify({
          threadId: active?.id,
          messageId: msgId,
          feedback,
          userToken: token,
          userQuestion: question,
          assistantResponse: response.slice(0, 2000),
          reason,
        }),
      });
    } catch (e) { console.warn('feedback:', e.message); }
  };

  const handleThumbDown = (msgId) => {
    if (feedbacks[msgId]) return;
    setFbDialog({ msgId, reason: '' });
  };

  const submitNegativeFeedback = () => {
    if (!fbDialog) return;
    sendFeedback(fbDialog.msgId, 'negative', fbDialog.reason || '');
    setFbDialog(null);
  };

  const renderMsg = msg => {
    const userText = msg.displayText || (typeof msg.content === 'string' ? msg.content : '');
    if (msg.role === 'user') return (
      <div key={msg.id} className="mrow user">
        <div className="mav user-av">
          {userPhoto ? <img src={userPhoto} alt="" /> : (user.name?.charAt(0)?.toUpperCase() || 'U')}
        </div>
        <div className="mbub user-bub">
          {msg.attachment?.previewUrl && <img src={msg.attachment.previewUrl} alt="" className="img-prev" />}
          {msg.attachment && !msg.attachment.isImage && <Pill att={msg.attachment} />}
          {msg.displayText && <p className="utext">{msg.displayText}</p>}
          {!msg.displayText && typeof msg.content === 'string' && <p className="utext">{msg.content}</p>}
          {userText && (
            <div className="msg-actions">
              <CopyButton text={userText} />
              <button className="msg-action-btn" title="Ask in new chat"
                onClick={() => copyToNewChat(userText)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><line x1="12" y1="8" x2="12" y2="14"/><line x1="9" y1="11" x2="15" y2="11"/></svg>
              </button>
            </div>
          )}
        </div>
      </div>
    );

    // Data response
    if (msg.type === 'data' && msg.data) return (
      <div key={msg.id} className="mrow asst">
        <div className="mav asst-av"><img src="/logo.png" alt="" className="asst-av-img" /></div>
        <div className="mbub asst-bub data-response-wrap">
          <DataResponse data={msg.data} token={token} authHdr={authHdr}
            userQuestion={(() => { const allMsgs = active?.messages || []; const idx = allMsgs.findIndex(m => m.id === msg.id); for (let i = idx - 1; i >= 0; i--) { if (allMsgs[i].role === 'user') return allMsgs[i].displayText || ''; } return ''; })()} />
          <div className="feedback-row">
            <button
              className={`feedback-btn ${feedbacks[msg.id] === 'positive' ? 'fb-positive' : ''}`}
              title="Good response"
              onClick={() => sendFeedback(msg.id, 'positive')}
              disabled={!!feedbacks[msg.id]}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbacks[msg.id] === 'positive' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>
            </button>
            <button
              className={`feedback-btn ${feedbacks[msg.id] === 'negative' ? 'fb-negative' : ''}`}
              title="Bad response"
              onClick={() => handleThumbDown(msg.id)}
              disabled={!!feedbacks[msg.id]}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbacks[msg.id] === 'negative' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10zM17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>
            </button>
          </div>
        </div>
      </div>
    );

    // Text / greeting response
    const txt = typeof msg.content === 'string'
      ? msg.content
      : (msg.content?.find?.(b => b.type === 'text')?.text || '');
    return (
      <div key={msg.id} className="mrow asst">
        <div className="mav asst-av"><img src="/logo.png" alt="" className="asst-av-img" /></div>
        <div className="mbub asst-bub">
          <MD content={txt} />
          {msg.usage && (
            <div className="token-usage">
              <span className="token-badge input-tokens">
                <span className="token-label">In</span>
                <span className="token-val">{msg.usage.input_tokens?.toLocaleString()}</span>
              </span>
              <span className="token-badge output-tokens">
                <span className="token-label">Out</span>
                <span className="token-val">{msg.usage.output_tokens?.toLocaleString()}</span>
              </span>
            </div>
          )}
          <div className="feedback-row">
            <button
              className={`feedback-btn ${feedbacks[msg.id] === 'positive' ? 'fb-positive' : ''}`}
              title="Good response"
              onClick={() => sendFeedback(msg.id, 'positive')}
              disabled={!!feedbacks[msg.id]}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbacks[msg.id] === 'positive' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>
            </button>
            <button
              className={`feedback-btn ${feedbacks[msg.id] === 'negative' ? 'fb-negative' : ''}`}
              title="Bad response"
              onClick={() => handleThumbDown(msg.id)}
              disabled={!!feedbacks[msg.id]}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbacks[msg.id] === 'negative' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10zM17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const grouped = useMemo(() => groupThreadsByDate(threads), [threads]);

  const firstName = user.givenName || user.name?.split(' ')[0] || 'there';

  return (
    <div className="shell">
      <aside className={`sb ${sbOpen ? 'open' : 'shut'}`}>
        <div className="sb-head">
          <div className="sb-brand">
            <img src="/logo.png" alt="" className="sb-logo-img" />
            <span className="sb-name">KOGNITION COBC</span>
            <button className="sb-collapse" onClick={() => setSbOpen(false)} title="Collapse sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <button className="btn-new" onClick={newThread}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            New chat
          </button>
          <div className="sb-tabs">
            <button className={`sb-tab ${sbTab === 'chat' ? 'active' : ''}`} onClick={() => setSbTab('chat')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              Chat
            </button>
            <button className={`sb-tab ${sbTab === 'assistant' ? 'active' : ''}`} onClick={() => setSbTab('assistant')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Assistant
            </button>
          </div>
        </div>

        {sbTab === 'chat' ? (
        <div className="sb-list">
          {threads.length === 0 && <p className="sb-empty">No conversations yet</p>}
          {grouped.map(group => (
            <div key={group.label} className="sb-group">
              <div className="sb-group-label">{group.label}</div>
              {group.threads.map(t => (
                <div key={t.id} className={`sb-item ${t.id === active?.id ? 'on' : ''}`} onClick={() => openThread(t)}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="sb-ico">
                    <path d="M14 1H2a1 1 0 00-1 1v9a1 1 0 001 1h3l2 3 2-3h5a1 1 0 001-1V2a1 1 0 00-1-1z"
                      stroke="currentColor" strokeWidth="1.4" fill="none"/>
                  </svg>
                  <span className="sb-ttl">{t.title}</span>
                  <button className="sb-del" onClick={e => delThread(t.id, e)}>✕</button>
                </div>
              ))}
            </div>
          ))}
        </div>
        ) : (
        <div className="sb-list sb-assistant-list">
          {ASSISTANT_QUESTIONS.map(cat => (
            <div key={cat.category} className="sb-group">
              <div className="sb-group-label">{cat.category}</div>
              {cat.questions.map(q => (
                <div key={q} className="sb-asst-card" onClick={() => askAssistantQuestion(q)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="sb-ico" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <span className="sb-asst-q">{q}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        )}
        <div className="sb-foot">
          <div className="sb-user">
            <div className="sb-uav">
              {userPhoto ? <img src={userPhoto} alt="" /> : (user.name?.charAt(0)?.toUpperCase() || 'U')}
            </div>
            <div className="sb-uinfo">
              <span className="sb-uname">{user.name}</span>
              <span className="sb-uemail">{user.email}</span>
            </div>
            <button className="btn-logout" onClick={onLogout} title="Sign out">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M16 17l5-5-5-5M21 12H9M13 2H5a2 2 0 00-2 2v16a2 2 0 002 2h8"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="btn-tog hamburger" onClick={() => setSbOpen(p => !p)} title="Toggle sidebar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <div className="tb-mid">
            <img src="/logo.png" alt="" className="tb-klogo-img" />
            <span className="tb-title">{active?.title || 'KOGNITION COBC'}</span>
          </div>
          {(totalUsage.input_tokens > 0 || totalUsage.output_tokens > 0) && (
            <div className="topbar-tokens">
              <span className="tt-badge tt-in">
                <span className="tt-label">In</span>
                <span className="tt-val">{totalUsage.input_tokens.toLocaleString()}</span>
              </span>
              <span className="tt-badge tt-out">
                <span className="tt-label">Out</span>
                <span className="tt-val">{totalUsage.output_tokens.toLocaleString()}</span>
              </span>
            </div>
          )}
          {/* Model label removed */}
        </header>

        <div className="chat">
          {msgs.length === 0 && !loading ? (
            <div className="welcome-center">
              <Meteors number={16} />
              <div className="wlc-content">
                <div className="wlc-left">
                  <div className="wlc-hero">
                    <img src="/logo.png" alt="" className="wlc-badge-lg-img" />
                    <h1 className="wlc-h1">{greet().greeting}, {firstName}</h1>
                    <p className="wlc-sub">{greet().subtitle}</p>
                  </div>
                  <div className="wlc-input-card">
                    {pending && (
                      <div className="pend-wrap">
                        {pending.type?.startsWith('image/') ? (
                          <div className="pend-img-wrap">
                            <img src={URL.createObjectURL(pending)} alt="" className="pend-img" />
                            <button className="pend-x" onClick={() => setPending(null)}>x</button>
                          </div>
                        ) : (
                          <Pill
                            att={{ fileName: pending.name, _displayName: pending._displayName || null,
                              _ext: pending._ext || pending.name.split('.').pop(), size: pending.size, isImage: false }}
                            onRemove={() => setPending(null)}
                          />
                        )}
                      </div>
                    )}
                    <div className="input-row">
                      <button className="btn-clip" onClick={() => fileRef.current?.click()}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                            stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <input ref={fileRef} type="file" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) setPending(f); e.target.value = ''; }}
                        accept="image/*,.pdf,.txt,.csv,.json,.md,.py,.js,.ts,.html,.css" />
                      <textarea ref={taRef} className="ta"
                        placeholder="Ask anything about COBC data..."
                        value={input} onChange={e => setInput(e.target.value)}
                        onKeyDown={kd} onPaste={onPaste} rows={1} disabled={loading} />
                      <button className={`btn-send ${loading || (!input.trim() && !pending) ? 'off' : ''}`}
                        onClick={() => send()} disabled={loading || (!input.trim() && !pending)}>
                        {loading
                          ? <span className="spin w" />
                          : <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                              <path d="M12 5v14M5 12l7-7 7 7" stroke="white" strokeWidth="2.3"
                                strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>}
                      </button>
                    </div>
                    <p className="wlc-hint"> &middot; KOGNITION SUITE &middot; COBC Data Analytics </p>
                  </div>
                </div>
                <div className="wlc-right">
                  <AnimatedTips />
                </div>
              </div>
            </div>
          ) : (
            <div className="msgs">
              {msgs.map(renderMsg)}
              {loading && (
                <div className="mrow asst">
                  <div className="mav asst-av"><img src="/logo.png" alt="" className="asst-av-img" /></div>
                  <div className="mbub asst-bub"><LoadingIndicator /></div>
                </div>
              )}
              {err && <div className="err-bar">{err}</div>}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {msgs.length > 0 && (
          <div className="input-zone">
            <div className="input-card">
              {pending && (
                <div className="pend-wrap">
                  {pending.type?.startsWith('image/') ? (
                    <div className="pend-img-wrap">
                      <img src={URL.createObjectURL(pending)} alt="" className="pend-img" />
                      <button className="pend-x" onClick={() => setPending(null)}>x</button>
                    </div>
                  ) : (
                    <Pill
                      att={{ fileName: pending.name, _displayName: pending._displayName || null,
                        _ext: pending._ext || pending.name.split('.').pop(), size: pending.size, isImage: false }}
                      onRemove={() => setPending(null)}
                    />
                  )}
                </div>
              )}
              <div className="input-row">
                <button className="btn-clip" onClick={() => fileRef.current?.click()}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <input ref={fileRef} type="file" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) setPending(f); e.target.value = ''; }}
                  accept="image/*,.pdf,.txt,.csv,.json,.md,.py,.js,.ts,.html,.css" />
                <textarea ref={taRef} className="ta"
                  placeholder="Ask anything about COBC data..."
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={kd} onPaste={onPaste} rows={1} disabled={loading} />
                <button className={`btn-send ${loading || (!input.trim() && !pending) ? 'off' : ''}`}
                  onClick={() => send()} disabled={loading || (!input.trim() && !pending)}>
                  {loading
                    ? <span className="spin w" />
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12l7-7 7 7" stroke="white" strokeWidth="2.3"
                          strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>}
                </button>
              </div>
            </div>
            <p className="input-hint"> &middot; KOGNITION SUITE &middot; COBC Data Analytics </p>
          </div>
        )}

        {/* Negative feedback reason dialog */}
        {fbDialog && (
          <div className="cell-modal-overlay" onClick={() => setFbDialog(null)}>
            <div className="fb-dialog" onClick={e => e.stopPropagation()}>
              <div className="fb-dialog-head">
                <span className="fb-dialog-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10zM17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg>
                  What went wrong?
                </span>
                <button className="cell-modal-close" onClick={() => setFbDialog(null)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div className="fb-dialog-body">
                <p className="fb-dialog-sub">Help us improve by telling us what was wrong with this response.</p>
                <div className="fb-quick-reasons">
                  {['Incorrect data', 'Wrong SQL query', 'Incomplete answer', 'Irrelevant response', 'Too slow'].map(r => (
                    <button key={r}
                      className={`fb-reason-chip ${fbDialog.reason === r ? 'active' : ''}`}
                      onClick={() => setFbDialog(p => ({ ...p, reason: p.reason === r ? '' : r }))}>
                      {r}
                    </button>
                  ))}
                </div>
                <textarea
                  className="fb-reason-input"
                  placeholder="Additional details (optional)..."
                  value={fbDialog.reason}
                  onChange={e => setFbDialog(p => ({ ...p, reason: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="fb-dialog-foot">
                <button className="fb-cancel-btn" onClick={() => setFbDialog(null)}>Cancel</button>
                <button className="fb-submit-btn" onClick={submitNegativeFeedback}>Submit Feedback</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function greet() {
  const h = new Date().getHours();
  const day = new Date().getDate();
  const month = new Date().getMonth();
  const seed = day + month * 31; // changes daily

  const morning   = ['Good morning', 'Rise and shine', 'A fresh start'];
  const afternoon = ['Good afternoon', 'Hope the day is treating you well', 'Afternoon'];
  const evening   = ['Good evening', 'Winding down the day?', 'Evening'];
  const night     = ['Working late?', 'Burning the midnight oil?', 'Night owl mode'];

  const subtitles = [
    'What COBC data would you like to explore today?',
    'Ask me anything about compliance cases, trends, or training.',
    'Ready to dive into the data. What would you like to know?',
    'I can query cases, violations, training, and more.',
    'Let\'s uncover insights from the COBC data.',
    'What compliance question is on your mind?',
    'From case trends to corrective actions \u2014 just ask.',
  ];

  let pool;
  if (h >= 5 && h < 12)       pool = morning;
  else if (h >= 12 && h < 17) pool = afternoon;
  else if (h >= 17 && h < 22) pool = evening;
  else                         pool = night;

  const greeting = pool[seed % pool.length];
  const subtitle = subtitles[seed % subtitles.length];
  return { greeting, subtitle };
}